import i18next from 'i18next';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  createFirstConsultation,
  fetchPatientFirstConsultation,
  removeFirstConsultationDocument,
  updateFirstConsultation,
  uploadFirstConsultationDocument,
} from '@/api/firstConsultations/firstConsultationsApi';
import type {
  FirstConsultation,
  FirstConsultationWritePayload,
} from '@/api/firstConsultations/firstConsultationsApi.types';
import {
  buildFirstConsultationVersionsQueryKey,
  buildPatientFirstConsultationQueryKey,
  buildPatientQueryKey,
  buildPatientTimelineQueryKey,
} from '@/constants/queryKeys';

export function usePatientFirstConsultationQuery(
  patientId: string
): UseQueryResult<FirstConsultation | null, Error> {
  return useQuery({
    queryKey: [...buildPatientFirstConsultationQueryKey(patientId), i18next.language],
    queryFn: function loadConsultation() {
      return fetchPatientFirstConsultation(patientId);
    },
    enabled: patientId.length > 0,
  });
}

function useFirstConsultationInvalidation() {
  const queryClient = useQueryClient();
  return function invalidate(consultation: FirstConsultation) {
    queryClient.invalidateQueries({
      queryKey: buildPatientFirstConsultationQueryKey(consultation.patientId),
    });
    queryClient.invalidateQueries({
      queryKey: buildFirstConsultationVersionsQueryKey(consultation.id),
    });
    queryClient.invalidateQueries({
      queryKey: buildPatientTimelineQueryKey(consultation.patientId),
    });
    queryClient.invalidateQueries({ queryKey: buildPatientQueryKey(consultation.patientId) });
  };
}

interface CreateFirstConsultationVariables {
  patientId: string;
  payload: FirstConsultationWritePayload;
}

export function useCreateFirstConsultationMutation(): UseMutationResult<
  FirstConsultation,
  Error,
  CreateFirstConsultationVariables
> {
  const invalidate = useFirstConsultationInvalidation();
  return useMutation({
    mutationFn: function create(variables: CreateFirstConsultationVariables) {
      return createFirstConsultation(variables.patientId, variables.payload);
    },
    onSuccess: invalidate,
  });
}

interface UpdateFirstConsultationVariables {
  consultationId: string;
  payload: FirstConsultationWritePayload;
}

export function useUpdateFirstConsultationMutation(): UseMutationResult<
  FirstConsultation,
  Error,
  UpdateFirstConsultationVariables
> {
  const invalidate = useFirstConsultationInvalidation();
  return useMutation({
    mutationFn: function update(variables: UpdateFirstConsultationVariables) {
      return updateFirstConsultation(variables.consultationId, variables.payload);
    },
    onSuccess: invalidate,
  });
}

interface UploadFirstConsultationDocumentVariables {
  consultationId: string;
  file: File;
}

export function useUploadFirstConsultationDocumentMutation(): UseMutationResult<
  FirstConsultation,
  Error,
  UploadFirstConsultationDocumentVariables
> {
  const invalidate = useFirstConsultationInvalidation();
  return useMutation({
    mutationFn: function upload(variables: UploadFirstConsultationDocumentVariables) {
      return uploadFirstConsultationDocument(variables.consultationId, variables.file);
    },
    onSuccess: invalidate,
  });
}

interface RemoveFirstConsultationDocumentVariables {
  consultationId: string;
  documentId: string;
}

export function useRemoveFirstConsultationDocumentMutation(): UseMutationResult<
  FirstConsultation,
  Error,
  RemoveFirstConsultationDocumentVariables
> {
  const invalidate = useFirstConsultationInvalidation();
  return useMutation({
    mutationFn: function remove(variables: RemoveFirstConsultationDocumentVariables) {
      return removeFirstConsultationDocument(variables.consultationId, variables.documentId);
    },
    onSuccess: invalidate,
  });
}
